import { getRouteApi } from '@tanstack/react-router';
import type { processDefinitionsArchivedQueryFragment$data } from './__generated__/processDefinitionsArchivedQueryFragment.graphql';
import type { TProcessDefinitionsArchivedSearchSchema } from './process-definitions-archived-search-schema';

export const processDefinitionsArchivedRouteApi = getRouteApi(
    '/_index/process-definitions-archived'
);

/**
 * It uses the search/query parameters of the location and the data from the query fragment to represent a list selection.
 * A validation function can be provided to check the entries of a list selection.
 * Returns undefined if a validation function is provided and a list selection contains an invalid entry.
 *
 * @param fragment A query fragment that contains data for a list
 * @param validate A validation function that checks the entries of a list selection
 */
export const useProcessDefinitionsArchivedSelectionState = (
    fragment?: processDefinitionsArchivedQueryFragment$data | null,
    validate?: (entry: processDefinitionsArchivedQueryFragment$data['content'][number]) => boolean
): TProcessDefinitionsArchivedSearchSchema['selected'] => {
    const selectedIds = processDefinitionsArchivedRouteApi.useSearch({
        select: (state) => state.selected,
    });

    if (!selectedIds || !fragment) {
        return undefined;
    }

    const selectedItems = fragment.content.filter((entry) => selectedIds.includes(entry.id));

    if (validate && !selectedItems.every(validate)) {
        return undefined;
    }

    return selectedItems.map((entry) => entry.id);
};

export const useProcessDefinitionsArchivedResetSelectionState = (): (() => void) => {
    const navigate = processDefinitionsArchivedRouteApi.useNavigate();
    return () => {
        // Navigate to the list and replace the current history entry to make it no longer accessible
        navigate({
            replace: true,
            search: (prevSearch) =>
                Object.assign(Object.assign({}, prevSearch), {
                    // remove selection
                    selected: undefined,
                }),
            to: '/process-definitions-archived',
        });
    };
};

export const useProcessDefinitionsArchivedAnySelected = (
    selectionState?: TProcessDefinitionsArchivedSearchSchema['selected']
): boolean => Array.isArray(selectionState) && selectionState.length > 0;
